import { useCallback, useEffect, useRef, useState } from 'react';
import Joyride, { ACTIONS, CallBackProps, EVENTS, STATUS, Step } from 'react-joyride';
import { Flex, Icon, Text, useColorModeValue } from '@chakra-ui/react';
import { MdPlayCircleOutline } from 'react-icons/md';
import { translate } from '../../../i18n';
import { useAuth } from '../../auth/AuthProvider';
import { useSeenTours } from '../hooks/useSeenTours';

type Props = {
  tourId: string;
  steps: Step[];
  onStepChange?: (stepIndex: number) => void;
};

export default function GuidedTour({ tourId, steps, onStepChange }: Props) {
  const { currentUser } = useAuth();
  const uid = currentUser?.sub as string | undefined;
  const { hasSeen, markSeen, resetTour } = useSeenTours(uid);
  const [replaying, setReplaying] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  const bgColor = useColorModeValue('#FFFFFF', '#1B2559');
  const textColor = useColorModeValue('#1B2559', '#FFFFFF');
  const primaryColor = useColorModeValue('#422AFB', '#7551FF');
  const bannerBg = useColorModeValue('brand.50', 'whiteAlpha.100');
  const bannerColor = useColorModeValue('brand.600', 'brand.200');

  const alreadySeen = hasSeen(tourId);
  const shouldRun = replaying || !alreadySeen;

  // Track whether the tour was actively running so we can mark it seen on unmount
  const wasRunningRef = useRef(false);
  wasRunningRef.current = shouldRun;

  useEffect(() => {
    return () => {
      // If the user navigates away while the tour is running, mark it as seen
      // so it doesn't replay every time they revisit the page
      if (wasRunningRef.current) {
        markSeen(tourId);
      }
    };
  }, [tourId, markSeen]);

  const goToStep = useCallback((nextIndex: number) => {
    onStepChange?.(nextIndex);
    // Let the DOM update (e.g. tab switch) before Joyride looks for the target
    setTimeout(() => setStepIndex(nextIndex), 50);
  }, [onStepChange]);

  const handleCallback = (data: CallBackProps) => {
    const { status, action, index, type } = data;
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED || action === ACTIONS.CLOSE) {
      markSeen(tourId);
      setReplaying(false);
      setStepIndex(0);
      return;
    }
    if (type === EVENTS.TARGET_NOT_FOUND) {
      // Skip past missing targets so the tour doesn't get stuck
      if (index < steps.length - 1) {
        goToStep(index + 1);
      } else {
        markSeen(tourId);
        setReplaying(false);
        setStepIndex(0);
      }
      return;
    }
    if (type === EVENTS.STEP_AFTER) {
      if (action === ACTIONS.NEXT) {
        goToStep(index + 1);
      } else if (action === ACTIONS.PREV) {
        goToStep(index - 1);
      }
    }
  };

  const handleReplay = () => {
    onStepChange?.(0);
    resetTour(tourId);
    setStepIndex(0);
    setTimeout(() => setReplaying(true), 100);
  };

  const styles = {
    options: {
      arrowColor: bgColor,
      backgroundColor: bgColor,
      primaryColor,
      textColor,
      zIndex: 10000,
    },
    tooltip: {
      borderRadius: '16px',
      padding: '20px',
      fontSize: '14px',
    },
    tooltipTitle: {
      fontSize: '16px',
      fontWeight: 700,
    },
    buttonNext: {
      borderRadius: '8px',
      fontSize: '13px',
      fontWeight: 600,
      padding: '8px 16px',
    },
    buttonBack: {
      color: textColor,
      fontSize: '13px',
      fontWeight: 600,
    },
    buttonSkip: {
      color: textColor,
      fontSize: '13px',
    },
  };

  if (steps.length === 0) return null;

  return (
    <>
      {alreadySeen && !replaying && (
        <Flex
          as="button"
          onClick={handleReplay}
          align="center"
          gap={2}
          px={4}
          py={2}
          bg={bannerBg}
          borderRadius="xl"
          cursor="pointer"
          transition="all 0.15s"
          _hover={{ opacity: 0.8 }}
          w="fit-content"
        >
          <Icon as={MdPlayCircleOutline} w="18px" h="18px" color={bannerColor} />
          <Text fontSize="sm" fontWeight="500" color={bannerColor}>
            {translate('walkthrough.replay_tour')}
          </Text>
        </Flex>
      )}
      {shouldRun && (
        <Joyride
          steps={steps}
          stepIndex={stepIndex}
          run={shouldRun}
          continuous
          showSkipButton
          showProgress
          callback={handleCallback}
          styles={styles}
          locale={{
            back: translate('walkthrough.tour_back'),
            close: translate('walkthrough.tour_close'),
            last: translate('walkthrough.tour_last'),
            next: translate('walkthrough.tour_next'),
            nextLabelWithProgress: translate('walkthrough.tour_next_with_progress'),
            skip: translate('walkthrough.tour_skip'),
          }}
          floaterProps={{
            disableAnimation: true,
          }}
        />
      )}
    </>
  );
}
